import { useState, useCallback, useEffect } from 'react';
import useServerApi from './useServerApi';

/**
 * Hook to fetch available Stripe plans and subscriptions.
 * Returns the same dict as fetchAvailablePlans, plus a reload function and loading/error states.
 * Usage:
 *   const { allProducts, activeProducts, subscriptions, loading, error, reload } = useAvailablePlans();
 *   reload() to refetch.
 */
export default function useAvailablePlans() {
    const [plans, setPlans] = useState({
        allProducts: [],
        activeProducts: [],
        subscriptions: [],
    });
    const [api, , , loading] = useServerApi();
    const [error, setError] = useState(null);

    const fetchPlans = useCallback(async () => {
        setError(null);
        const [success, response, err] = await api.get('/rest_endpoints/stripe/crud/available_plans');
        if (success) {
            const products = (response.products || []).map((p: any) => ({
                id: p.id,
                priceId: p.price.id,
                priceAmount: p.price.unit_amount / 100,
                name: p.name,
                features: p.features || [],
                description: p.description || '',
                is_active: p.is_active,
            })).sort((a: any, b: any) => a.priceAmount - b.priceAmount);
            setPlans({
                allProducts: products,
                activeProducts: products.filter((prod: any) => prod.is_active),
                subscriptions: response.subscriptions || [],
            });
        } else {
            setPlans({
                allProducts: [],
                activeProducts: [],
                subscriptions: [],
            });
            setError(err);
        }
    }, [api]);

    useEffect(() => {
        fetchPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        ...plans,
        loading,
        error,
        reload: fetchPlans,
    };
}
